// Identify phase — preload-and-route. docs/fe-state-machine.md#identify.
//
// Boot has already done /v1/check and stashed `theme`, `isRegistered`, and
// (when registered) `assets.character` + `isMale` into ctx. Identify just
// preloads the shared assets for the picked theme, then routes to Download
// (registered) or Capture (new).
//
// The same bg + 9:16 frame layout from Boot is mounted here so the transition
// feels like a quiet "loading…" beat rather than a screen swap.

import type { Phase } from "../state/types";
import { apiBase } from "../api/client";
import { mountFrame, clearChildren } from "../ui/overlay";
import { AssetResolver } from "../assets/AssetResolver";
import { getAudioBus } from "../audio";
import { buildCharacterSprites } from "../game/buildCharacter";

let cleanup: (() => void) | null = null;

export const Identify: Phase = {
  async enter(ctx, transition) {
    const root = document.getElementById("app")!;
    clearChildren(root);

    const bg = document.createElement("div");
    bg.className = "app-bg";
    bg.style.backgroundImage = "url(/textures/bg_indexA.png)";
    bg.style.backgroundColor = "#f7c7d8";

    const frame = document.createElement("div");
    frame.className = "app-frame";
    frame.style.background = "#fff";
    frame.style.color = "#222";
    frame.style.padding = "20px";
    frame.style.boxSizing = "border-box";
    frame.style.alignItems = "center";
    frame.style.justifyContent = "center";
    frame.style.gap = "16px";

    frame.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;gap:14px;padding:24px;border-radius:14px;background:#f4f4f4;color:#222;">
        <div class="spinner" style="
          width:44px;height:44px;border-radius:50%;
          border:3px solid #0001;border-top-color:#e34;
          animation:spin 0.9s linear infinite;"></div>
        <div id="status" style="font-size:14px;opacity:0.9;">Đang tải tài nguyên…</div>
      </div>
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    `;

    root.appendChild(bg);
    const frameCleanup = mountFrame(root, frame);
    cleanup = () => {
      if (bg.parentElement === root) root.removeChild(bg);
      frameCleanup();
    };

    const status = frame.querySelector<HTMLElement>("#status")!;
    const resolver = new AssetResolver(apiBase());
    const audioCtx = getAudioBus().ctx;

    try {
      const shared = await resolver.preloadShared(ctx.theme, audioCtx);
      if (ctx.characterMode === "custom") {
        // Custom: go to the capture hub (captures the player's face).
        transition("Capture", { assets: { ...ctx.assets, shared } });
      } else {
        // Template (Nhân vật mẫu): no capture — always the DEFAULT character,
        // for new AND returning players (forceDefaultHeads), then show the guide.
        // A returning player's own face is played via the custom path instead.
        status.textContent = "Đang tạo nhân vật…";
        const characterSprites = await buildCharacterSprites(
          { ...ctx, assets: { ...ctx.assets, shared } },
          shared,
          true,
        );
        transition("Instructions", {
          assets: { ...ctx.assets, shared, characterSprites },
        });
      }
    } catch (err) {
      console.error("[Identify] preload/bake failed", err);
      status.textContent = "Tải tài nguyên thất bại — kiểm tra mạng và tải lại.";
    }
  },
  exit() {
    cleanup?.();
    cleanup = null;
  },
};
